import { apiService } from './api.service.js'
import { cacheService } from './cache.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de responsáveis com cache inteligente
 * Otimiza leituras do Firestore mantendo dados no localStorage
 */
export class ResponsavelCacheService {
  constructor() {
    this.cacheKey = 'responsaveis'
    this.endpoint = 'responsaveis'
    this.loadingPromise = null // Previne chamadas simultâneas
  }

  /**
   * Lista responsáveis com cache inteligente (TTL 10 minutos)
   * @returns {Promise<Array>} Lista de responsáveis
   */
  async listar() {
    // Verifica se cache existe e não expirou (10 minutos)
    if (this._isCacheValid()) {
      return cacheService.get(this.cacheKey)
    }

    // Se já está carregando, aguarda a mesma promise
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Cache expirado ou não existe: busca do backend
    
    this.loadingPromise = this._loadFromBackend()
    const result = await this.loadingPromise
    this.loadingPromise = null
    
    return result
  }

  /**
   * Carrega dados do backend (método privado).
   * Em caso de timeout de autenticação (AUTH_TIMEOUT), retorna dados do cache local
   * ou array vazio — sem propagar exceção e sem executar requisição à API.
   *
   * @returns {Promise<Array>} Lista de responsáveis
   * @private
   */
  async _loadFromBackend () {
    try {
      const response = await apiService.get(this.endpoint)
      const responsaveis = response.data || response || []

      // Salva no cache com timestamp
      cacheService.set(this.cacheKey, responsaveis)

      return responsaveis
    } catch (error) {
      // Em timeout de auth, retorna cache local ou array vazio (Req 5.2)
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        const cacheLocal = cacheService.get(this.cacheKey)
        return cacheLocal || []
      }
      // Se erro de autenticação, retorna array vazio sem cachear
      if (error.message?.includes('autorizado')) {
        return []
      }
      throw error
    }
  }

  /**
   * Verifica se cache é válido (não expirou)
   * @returns {boolean} True se cache válido
   * @private
   */
  _isCacheValid() {
    if (!cacheService.currentUserUid) return false
    
    try {
      const userKey = cacheService._getUserKey(this.cacheKey)
      const cached = localStorage.getItem(userKey)
      if (!cached) return false
      
      const cacheData = JSON.parse(cached)
      
      // Verifica se pertence ao usuário atual
      if (cacheData.uid !== cacheService.currentUserUid) return false
      
      const now = Date.now()
      const cacheAge = now - cacheData.timestamp
      const TTL = 60 * 60 * 1000 // 1 hora
      
      return cacheAge < TTL
    } catch (error) {
      return false
    }
  }

  /**
   * Cria novo responsável (backend + cache)
   * @param {Object} dadosResponsavel - Dados do responsável
   * @returns {Promise<Object>} Responsável criado
   */
  async criar(dadosResponsavel) {
    const response = await apiService.post(this.endpoint, dadosResponsavel)
    // apiService retorna o corpo JSON direto (não envelopado em .data)
    const novoResponsavel = response.data || response
    
    // Adiciona ao cache
    cacheService.addToArray(this.cacheKey, novoResponsavel)
    
    // Dispara evento para UI reagir
    window.dispatchEvent(new CustomEvent('responsaveis-cache-updated'))
    
    return novoResponsavel
  }

  /**
   * Atualiza responsável (backend + cache)
   * @param {string} id - ID do responsável
   * @param {Object} dadosAtualizacao - Dados para atualizar
   * @returns {Promise<Object>} Responsável atualizado
   */
  async atualizar(id, dadosAtualizacao) {
    const response = await apiService.put(`${this.endpoint}/${id}`, dadosAtualizacao)
    const responsavelAtualizado = response.data || response
    
    // Atualiza no cache
    cacheService.updateInArray(this.cacheKey, id, responsavelAtualizado)
    
    return responsavelAtualizado
  }

  /**
   * Exclui responsável (backend + cache)
   * @param {string} id - ID do responsável
   * @returns {Promise<boolean>} True se excluído
   */
  async excluir(id) {
    await apiService.delete(`${this.endpoint}/${id}`)
    
    // Remove do cache
    cacheService.removeFromArray(this.cacheKey, id)
    
    return true
  }

  /**
   * Busca responsável por ID (do cache)
   * @param {string} id - ID do responsável
   * @returns {Object|null} Responsável encontrado
   */
  buscarPorId(id) {
    const responsaveis = cacheService.get(this.cacheKey) || []
    return responsaveis.find(responsavel => responsavel.id === id) || null
  }

  /**
   * Força recarregamento do cache
   * @returns {Promise<Array>} Lista atualizada
   */
  async recarregar() {
    cacheService.remove(this.cacheKey)
    return this.listar()
  }

  /**
   * Inicializa cache no startup do sistema
   * @returns {Promise<void>}
   */
  async inicializarCache() {
    try {
      // Só inicializa se não tem cache válido
      if (!this._isCacheValid()) {
        await this._loadFromBackend()
      }
    } catch (error) {
      console.error('❌ Erro ao inicializar cache de responsáveis:', error)
    }
  }

  /**
   * Limpa cache de responsáveis
   */
  limparCache() {
    cacheService.remove(this.cacheKey)
  }
}

// Instância singleton
export const responsavelCacheService = new ResponsavelCacheService()